import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import api from '../services/api';
import '../styles/pages/CareersPage.css';

const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [activeCategory, currentPage]);

  const fetchCategories = async () => {
    try {
      const response = await api.getJobCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching job categories:', error);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await api.getJobs({
        category_id: activeCategory,
        status: 'Open',
        page: currentPage,
        limit: 9
      });

      if (response.success) {
        setJobs(response.data);
        setTotalPages(response.pagination.total_pages);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setCurrentPage(1);
  };

  // Filter jobs by search query
  const filteredJobs = jobs.filter(job => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      job.title.toLowerCase().includes(query) ||
      job.description.toLowerCase().includes(query) ||
      job.location.toLowerCase().includes(query) ||
      job.employment_type.toLowerCase().includes(query)
    );
  });

  return (
    <PageWrapper>
      <SEO
        title="Careers - Join Supernova Lab"
        description="Explore career opportunities at Supernova Lab. Join our team of innovators and help shape Barbados' future."
      />

      <div className="careers-page">
        {/* Header */}
        <section className="careers-header">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="careers-badge">Opportunities</span>
              <h1 className="careers-title">Build The Future With Us</h1>
              <p className="careers-description">
                Join a team of innovators, researchers, and changemakers<br />
                working to transform Barbados through experimentation and R&D
              </p>
            </motion.div>
          </div>
        </section>

        {/* Search & Filter - ON SAME LINE */}
        <section className="careers-filters">
          <div className="container">
            <div className="search-filter-wrapper">
              {/* Search Input */}
              <div className="search-input-container">
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search jobs by title, location, or type..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Category Dropdown */}
              <select
                className="category-dropdown"
                value={activeCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Jobs Grid */}
        <section className="jobs-section">
          <div className="container">
            {loading ? (
              <div className="text-center py-5">
                <i className="fas fa-spinner fa-spin fa-3x"></i>
                <p className="mt-3">Loading positions...</p>
              </div>
            ) : filteredJobs.length > 0 ? (
              <>
                <div className="jobs-grid">
                  {filteredJobs.map(job => (
                    <motion.div
                      key={job.id}
                      className="job-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="job-card-header">
                        {/* Only show featured badge if featured is true/1 */}
                        {job.featured === 1 && (
                          <span className="featured-badge">
                            <i className="fas fa-star me-1"></i>Featured
                          </span>
                        )}
                        {job.category_name && (
                          <span className="job-category-badge" style={{ backgroundColor: job.category_color }}>
                            {job.category_name}
                          </span>
                        )}
                      </div>

                      <h3 className="job-title">{job.title}</h3>

                      <div className="job-meta">
                        <span><i className="fas fa-map-marker-alt me-2"></i>{job.location}</span>
                        <span><i className="fas fa-briefcase me-2"></i>{job.employment_type}</span>
                        <span><i className="fas fa-layer-group me-2"></i>{job.experience_level}</span>
                        {/* Only show remote if remote_allowed is true/1 */}
                        {job.remote_allowed === 1 && (
                          <span className="remote"><i className="fas fa-laptop-house me-2"></i>Remote OK</span>
                        )}
                      </div>

                      <p className="job-excerpt">
                        {job.description.replace(/<[^>]*>/g, '').substring(0, 150)}...
                      </p>

                      <div className="job-card-footer">
                        {job.salary_range && (
                          <span className="salary-range">
                            <i className="fas fa-dollar-sign me-2"></i>{job.salary_range}
                          </span>
                        )}
                        <Link to={`/careers/${job.slug}`} className="apply-btn">
                          View Details <i className="fas fa-arrow-right ms-2"></i>
                        </Link>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="pagination-container">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-chevron-left"></i> Previous
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next <i className="fas fa-chevron-right ms-2"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-jobs">
                <i className="fas fa-briefcase fa-4x mb-4"></i>
                <h3>No Positions Found</h3>
                <p>
                  {searchQuery ? 
                    `No jobs found matching "${searchQuery}". Try a different search.` :
                    'Check back soon for new opportunities!'}
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </PageWrapper>
  );
};

export default CareersPage;
